import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useMedia from "react-use/lib/useMedia";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_XS } from "@/core/media";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { formatFullName } from "@/data/persons";
import { IUserEmail, resendEmailConfirmation } from "@/data/session";
import { deleteUserEmail, markEmailAsPrimary } from "@/data/users";
import { Flex } from "@/eworkitComponents";
import { useToasts } from "@/views/components/Toast/useToasts";
import { EmailHandlingModal } from "@/views/Settings/UserSettings/UserAccount/UserName/EmailHandlingModal";

interface ISingleEmailSectionProps {
  email: IUserEmail;
  consultantProfiles: IConsultantProfile[];
  onUpdate: () => void;
}

export const SingleEmailSection: FC<ISingleEmailSectionProps> = ({ email, consultantProfiles, onUpdate }) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);
  const [isMarkAsPrimaryModalOpen, setMarkAsPrimaryModalOpen] = useState(false);

  const resendVerification = async () => {
    try {
      await resendEmailConfirmation({}, { email: email.email });
      addToast({ text: t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.VERIFICATION_SENT"), type: "success" });
    } catch (e) {
      addToast({ text: t("common:ERROR_MESSAGES.GENERAL"), type: "warning" });
    }
  };

  const removeEmailHandler = useCallback(() => {
    setRemoveModalOpen(true);
  }, []);

  const markAsPrimaryEmailHandler = useCallback(() => {
    setMarkAsPrimaryModalOpen(true);
  }, []);

  const handleModalSubmit = useCallback(
    setModalOpen => () => {
      onUpdate();
      setModalOpen(false);
    },
    [],
  );

  const isVerySmallScreen = useMedia(MEDIA_SIZE_XS);

  return (
    <Flex direction="row" justify="space-between">
      <Flex direction="column" innerMargin="sm">
        <Text>{email.email}</Text>
        {consultantProfiles.map(cp => (
          <Flex key={cp.id} direction="row" innerMargin="xs">
            <Icon color="grey2" icon="user" size="md" />
            <Text color="grey1">{formatFullName(cp)}</Text>
            <Text color="grey1">
              {`(${
                cp.sharedProfile?.company
                  ? cp.sharedProfile?.company.name
                  : cp.requestToJoin
                  ? t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.REQUEST_TO_JOIN_SENT", {
                      company: cp.requestToJoin.company.name,
                    })
                  : t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.PRIVATE_PERSON")
              })`}
            </Text>
          </Flex>
        ))}
      </Flex>
      <Flex>
        {email.primary ? (
          <Text color="grey1">{t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.PRIMARY")}</Text>
        ) : email.confirmed ? (
          <Flex direction={isVerySmallScreen ? "column" : "row"} innerMargin="md">
            <Button variant="secondary" onClick={markAsPrimaryEmailHandler}>
              {t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.MAKE_PRIMARY_BUTTON")}
            </Button>
            <Button variant={isVerySmallScreen ? "secondary" : "clean"} onClick={removeEmailHandler}>
              {isVerySmallScreen ? (
                t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.REMOVE_EMAIL_BUTTON")
              ) : (
                <Icon color="grey2" icon="close" size="lg" />
              )}
            </Button>
          </Flex>
        ) : (
          <Flex direction={isVerySmallScreen ? "column" : "row"} innerMargin="md">
            <Button variant="secondary" onClick={resendVerification}>
              {t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.RESEND_VERIFICATION_BUTTON")}
            </Button>
            <Button variant={isVerySmallScreen ? "secondary" : "clean"} onClick={removeEmailHandler}>
              {isVerySmallScreen ? (
                t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.REMOVE_EMAIL_BUTTON")
              ) : (
                <Icon color="grey2" icon="close" size="lg" />
              )}
            </Button>
          </Flex>
        )}
      </Flex>

      <EmailHandlingModal
        variant="REMOVE"
        email={email.email}
        onSubmit={deleteUserEmail}
        afterSubmit={handleModalSubmit(setRemoveModalOpen)}
        onClose={() => {
          setRemoveModalOpen(false);
        }}
        open={isRemoveModalOpen}
        size="xxs"
      />

      <EmailHandlingModal
        variant="MAKE_PRIMARY"
        email={email.email}
        onSubmit={markEmailAsPrimary}
        afterSubmit={handleModalSubmit(setMarkAsPrimaryModalOpen)}
        onClose={() => {
          setMarkAsPrimaryModalOpen(false);
        }}
        open={isMarkAsPrimaryModalOpen}
        size="xxs"
      />
    </Flex>
  );
};
